/**
 * CRM hub content — source for /crm and /crm/:slug routes.
 * Drives src/pages/CrmHub.tsx and src/pages/crm/CrmGuidePage.tsx,
 * and is mirrored in scripts/lib/buildSitemapEntries.ts.
 *
 * All copy is final English, ≥1000 visible words per subpage.
 * No Supabase calls anywhere — purely static SEO landing content.
 */

export type CrmMockupKind =
  | "kanban"
  | "outreach"
  | "notes"
  | "dealflow"
  | "notifications"
  | "chat"
  | "timeline"
  | "todos"
  | "templates"
  | "reports";

export interface CrmFeatureBlock {
  icon: "Kanban" | "Mail" | "Bell" | "MessageSquare" | "Clock" | "ListTodo" | "FileText" | "Send" | "BarChart3" | "Users" | "Filter" | "Calendar" | "Sparkles" | "Shield" | "Pin" | "Search" | "Layers" | "Zap" | "Eye" | "FolderOpen";
  heading: string;
  body: string;
}

export interface CrmHowStep {
  step: number;
  title: string;
  body: string;
}

export interface CrmBenefit {
  heading: string;
  body: string;
}

export interface CrmFaq {
  question: string;
  answer: string;
}

export interface CrmGuideEntry {
  slug: string;
  path: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  heroSubtitle: string;
  excerpt: string;
  /** Long intro paragraph after hero, before feature blocks. */
  intro: string;
  features: CrmFeatureBlock[];
  howItWorks: CrmHowStep[];
  mockup: { kind: CrmMockupKind; caption: string };
  benefits: CrmBenefit[];
  faqs: CrmFaq[];
  /** Closing prose paragraph above the CTA banner. */
  closing: string;
}

export const CRM_GUIDES: CrmGuideEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "investor-pipeline",
    path: "/crm/investor-pipeline",
    metaTitle: "Investor Pipeline CRM for Startups: Fundraising Kanban Board",
    metaDescription:
      "Run your entire fundraising pipeline in one Kanban board built for startup founders — six investor stages, drag-and-drop, deadlines, auto-notifications and direct database integration.",
    h1: "Investor Pipeline: A Kanban CRM Built for Startup Fundraising",
    heroSubtitle:
      "Six fundraising-specific stages, drag-and-drop investor cards, deadline tracking and auto-notifications — designed for founders, not for SDR teams.",
    excerpt:
      "The Kanban that actually mirrors how rounds close: Initial Contact, Sent Teaser, Dataroom before LOI, Due Diligence after LOI, Final Negotiations, Closed.",
    intro:
      "Generic CRM tools were built for repeatable B2B sales motions. Fundraising is not that. A round is a finite, weeks-long campaign where each investor is in a fundamentally different state — some have just received the teaser, some are signing the LOI, some are negotiating the final term sheet — and a founder needs to know the exact status of every conversation at a glance. CAPLINK's investor pipeline gives you exactly that: a Kanban board with the six stages every European startup fundraising process actually goes through, with investor cards that surface the data you need to act on next.",
    features: [
      {
        icon: "Kanban",
        heading: "Six fundraising-native stages",
        body: "Initial Contact, Sent Teaser, Dataroom before LOI, Due Diligence after LOI, Final Negotiations, and Closed. These are not generic 'qualified' and 'opportunity' columns retrofitted from sales software — they reflect the actual gating events in a venture round, so the position of a card tells the whole team what needs to happen next without any explanation.",
      },
      {
        icon: "Users",
        heading: "Direct investor database integration",
        body: "Every card is connected to the CAPLINK investor database. Drag an investor in from search, and the focus areas, ticket range, portfolio fit and partner profile come with them. No copy-pasting from LinkedIn, no stale contact lists, no duplicate records across spreadsheets — the investor record is the single source of truth.",
      },
      {
        icon: "Bell",
        heading: "Auto-notifications for both sides",
        body: "When you move a card forward, the investor sees a tailored update — dataroom access, document refresh, scheduled call — and your team gets reminders to follow up. Both sides stay synchronized without anyone writing a manual status email, and missed handoffs that normally kill momentum simply stop happening.",
      },
      {
        icon: "Clock",
        heading: "Days-in-stage and last-contact tracking",
        body: "Each card surfaces how long the investor has been in the current stage and when you last spoke. Filter the board by 'no contact in 7 days' or 'stuck >14 days in Due Diligence' to see exactly which conversations are going cold while there is still time to recover them, instead of finding out at the next investor update.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Pull investors from the database into the pipeline",
        body: "Search the integrated investor database by stage, geography, ticket size and sector. One click adds an investor to the Initial Contact column with the full firm profile, partner name and last interaction history already attached — no manual data entry required.",
      },
      {
        step: 2,
        title: "Move cards through the six stages as conversations progress",
        body: "Drag a card from Sent Teaser to Dataroom before LOI and CAPLINK grants the configured dataroom permissions, fires the right notification template and starts the days-in-stage counter. The stage transition is the trigger — you do not have to remember three separate actions.",
      },
      {
        step: 3,
        title: "Manage deadlines, todos and calendar sync per investor",
        body: "Set both general deadlines (round close date) and individual deadlines (this investor's commitment date) on any card. Deadlines flow into the CAPLINK calendar module and into the investor's view, and todos attached to each stage make sure nothing is skipped before the card moves forward.",
      },
    ],
    mockup: {
      kind: "kanban",
      caption:
        "A typical late-stage Series A pipeline view inside CAPLINK — six stages, color-coded days-in-stage badges and overdue deadline indicators.",
    },
    benefits: [
      {
        heading: "Never lose a hot investor to silence",
        body: "Last-contact filters and days-in-stage badges make it impossible for a warm conversation to drift for three weeks unnoticed. The founders who close fastest are not the ones with the most investors — they are the ones who never let momentum die.",
      },
      {
        heading: "One source of truth for the whole founding team",
        body: "Co-founders, advisors and finance leads all look at the same board, so the standard 'where are we with Sequoia?' question is answered by glancing at a card rather than triggering a Slack thread. Updates land in one place and stay there.",
      },
      {
        heading: "Round-close in weeks, not quarters",
        body: "Founders who run their pipeline in CAPLINK consistently report closing rounds 30–50% faster than peers running Notion boards or generic CRMs, because the entire process — outreach, dataroom, notes, deadlines — happens in one connected system.",
      },
    ],
    faqs: [
      {
        question: "How is this different from a HubSpot or Salesforce pipeline?",
        answer:
          "HubSpot and Salesforce are built for sales — repeatable B2B deals with discovery, demo, proposal, close. Fundraising has different gates (teaser, dataroom, LOI, due diligence, final terms), different stakeholders (partners, not procurement) and a hard deadline (the round close). CAPLINK's stages, notification templates and dataroom integration are built specifically for that flow, so founders are not bending a sales tool into a shape it was not designed for.",
      },
      {
        question: "Can I customize the six stages?",
        answer:
          "Yes. The six default stages cover the standard European fundraising motion, but every stage label, order and color can be adjusted, and you can add custom stages for a specific round if your process needs them — for example, a separate 'Reference Calls' column between Due Diligence and Final Negotiations.",
      },
      {
        question: "Does the pipeline sync with calendar reminders?",
        answer:
          "Deadlines set on any investor card automatically appear in the CAPLINK calendar module for the founder side and in the investor's own view of the deal. Push reminders fire 7, 3 and 1 day before a deadline, and calendar events for scheduled calls or meetings link back to the source card so the context is one click away.",
      },
      {
        question: "Can multiple co-founders work in the same pipeline?",
        answer:
          "Yes. The pipeline is shared across the founding team by default. Each user has a role, an avatar and a configurable view of which investors they own, and the activity log records every stage change so co-founders always know what their counterpart did last.",
      },
      {
        question: "What happens to investors when the round closes?",
        answer:
          "Closed cards are archived to the historical pipeline and remain in the investor's full timeline, so when you raise the next round the prior conversation, notes and decline reasons are immediately accessible. Nothing is lost, and bridging from Series A to Series B starts with a warm record rather than a cold list.",
      },
    ],
    closing:
      "The investor pipeline is the heart of the CAPLINK CRM. Combined with mass outreach, notes, deal flow and auto-notifications, it is the operating system founders use to run a professional fundraising process end-to-end.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "mass-outreach",
    path: "/crm/mass-outreach",
    metaTitle: "AI-Powered Mass Outreach for Startup Fundraising | CAPLINK CRM",
    metaDescription:
      "Send 1:1-quality investor outreach at pipeline scale. CAPLINK's AI customizes each email per investor focus, portfolio fit and stage preference, with open and click tracking built in.",
    h1: "Mass Investor Outreach That Reads Like a Personal Email",
    heroSubtitle:
      "AI customizes every message per investor based on focus, portfolio fit and stage preference — so 80 emails feel like 80 hand-written ones, with open and click tracking baked in.",
    excerpt:
      "Build a campaign from a CRM list, let the AI tailor each message to the investor, schedule follow-ups and watch the analytics dashboard.",
    intro:
      "The honest reason most startup outreach fails is not that founders write bad emails — it is that they write the same email to 80 investors who care about completely different things. A climate-tech fund does not want the SaaS metrics pitch, and a US growth-stage investor does not want the European seed teaser. CAPLINK's mass outreach module lets you build a campaign from your CRM pipeline once, then uses AI to rewrite each individual email so it speaks to that specific investor's focus area, ticket band, portfolio composition and stage preference. The result is outreach that reads like a 1:1 message because, functionally, it is one — without consuming an entire week of founder time.",
    features: [
      {
        icon: "Send",
        heading: "Campaigns built from your pipeline",
        body: "Select any CRM segment — by stage, geography, sector or saved search — and turn it into a campaign in two clicks. The campaign inherits the pipeline context, so when an investor replies, the reply is logged against their existing card rather than vanishing into a separate inbox.",
      },
      {
        icon: "Sparkles",
        heading: "AI personalization per investor",
        body: "Write one base message, and the AI rewrites it for each recipient using their focus areas, recent investments, stage preference and ticket range. Every email opens with a relevant hook ('We saw your investment in X') and closes with a CTA that matches their typical engagement pattern.",
      },
      {
        icon: "BarChart3",
        heading: "Open, click and reply tracking",
        body: "A full analytics dashboard shows opens, clicks per link, replies and bounces in real time. You see which subject lines convert, which investors are reading multiple times before replying, and which segments are not engaging — so the next batch is informed by data, not guesswork.",
      },
      {
        icon: "Clock",
        heading: "Automated follow-up sequencing",
        body: "Schedule one, two or three follow-ups per recipient with custom delays, conditional on whether the investor opened or replied. Drop-outs after the third follow-up move to a 'cooled' segment automatically, keeping the active pipeline clean without manual housekeeping.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Pick a segment and a campaign template",
        body: "From the pipeline, filter to the investors you want to reach — for example, all European Series A funds in fintech you have not contacted yet — and choose a template from your library (Teaser, NDA, Pitchdeck, Reminder).",
      },
      {
        step: 2,
        title: "Let the AI personalize each message",
        body: "Preview the AI-generated variant for any investor before sending. Tweak the intro paragraph if you want, lock the rest, then approve the batch. Founders typically review 5–10 sample variants and ship the rest in one click.",
      },
      {
        step: 3,
        title: "Track responses and trigger follow-ups",
        body: "The dashboard updates in real time. Replies land in the connected pipeline card; non-openers get the scheduled follow-up automatically; engaged investors are flagged for direct attention. You see the conversion funnel of the campaign at any moment.",
      },
    ],
    mockup: {
      kind: "outreach",
      caption:
        "The campaign builder showing the AI-personalized preview for an individual investor alongside the base template.",
    },
    benefits: [
      {
        heading: "10x outreach throughput at 1:1 quality",
        body: "Founders consistently send 60–100 personalized investor emails in an afternoon instead of three days, while reply rates stay at or above hand-written baselines because the AI personalization is genuinely contextual.",
      },
      {
        heading: "Cleaner inbox, cleaner pipeline",
        body: "Replies are attached to the investor card, not stranded in Gmail. The pipeline stays the source of truth and outreach activity is visible to the entire founding team without anyone forwarding emails around.",
      },
      {
        heading: "Data that compounds across rounds",
        body: "Campaign analytics persist. By Series B you have years of investor-response data telling you which firms actually engage, which partners reply on weekends, and which subject lines outperform — institutional memory you can not get from cold email tools.",
      },
    ],
    faqs: [
      {
        question: "Will investors detect AI-generated emails?",
        answer:
          "The AI rewrites the message using your voice and the investor's context — it does not generate generic 'as an AI language model' prose. The output reads like a thoughtful founder email because the source material is yours and the personalization variables come from real investor data. Reply rates in practice match or exceed hand-written outreach.",
      },
      {
        question: "Can I send from my own domain?",
        answer:
          "Yes. CAPLINK sends through your authenticated sending domain (SPF, DKIM, DMARC configured during setup), so replies route back to your inbox and investors see your address as the sender. Deliverability is identical to sending from Gmail or Outlook directly.",
      },
      {
        question: "How is open and click tracking handled for privacy?",
        answer:
          "Tracking is implemented via standard pixel and redirect mechanisms, fully compliant with GDPR. Founders can disable tracking for specific campaigns or investors who request it, and tracking data is never sold or shared.",
      },
      {
        question: "Can I review every personalized email before sending?",
        answer:
          "Yes. The default mode shows the AI-personalized preview for each recipient, and you can edit any individual message inline before approval. Power users typically spot-check 10–20% of a batch and ship the rest in bulk.",
      },
      {
        question: "Does this integrate with the rest of the CRM?",
        answer:
          "Tightly. Campaigns are built from pipeline segments, replies land on investor cards, opens and clicks are visible in the investor timeline, and engaged investors automatically advance to the next pipeline stage if you configure that rule. Outreach is not a separate tool — it is a function of the CRM.",
      },
    ],
    closing:
      "Mass outreach is the single highest-leverage CRM action in fundraising. CAPLINK makes the leverage compatible with the personal-touch quality investors actually respond to.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "investor-notes",
    path: "/crm/investor-notes",
    metaTitle: "Investor Notes & Meeting Summaries CRM | CAPLINK",
    metaDescription:
      "Per-investor timestamped notes, meeting summaries, internal tags, follow-up reminders and a last-contact field that keeps every fundraising conversation warm.",
    h1: "Investor Notes: Keep Every Conversation Warm and Auditable",
    heroSubtitle:
      "Timestamped notes, meeting summaries, internal tags and follow-up reminders attached to every investor card — with a last-contact field that surfaces the leads going cold.",
    excerpt:
      "Notes, meeting summaries, tags, pinned items and a last-contact reminder accessible directly from each investor profile card.",
    intro:
      "Fundraising is a memory problem disguised as a process problem. The reason a Series A partner replies 'remind me what your ARR was last time we spoke?' is not that they are dismissive — it is that they take forty meetings a week and you took one with them six weeks ago. The founder who walks into the second meeting with the exact prior context wins the round. CAPLINK's investor notes module makes that context impossible to lose: every meeting, call and email gets a timestamped, taggable, searchable record attached directly to the investor's pipeline card.",
    features: [
      {
        icon: "FileText",
        heading: "Timestamped meeting notes",
        body: "Every note carries the author, the date, the meeting type (intro call, partner pitch, follow-up) and an optional structured summary. Threaded under the investor card, the full history is a scroll away — no separate Notion page, no buried Google Doc, no 'where did we put that?'.",
      },
      {
        icon: "Pin",
        heading: "Pin, edit, delete with audit trail",
        body: "Pin the three most important notes to the top of the investor card so they are always the first thing you see. Edits are tracked, deletions are soft, and every change is recorded — the team always knows who wrote what and when.",
      },
      {
        icon: "Bell",
        heading: "Follow-up reminders built in",
        body: "Tag a note with a follow-up date and CAPLINK turns it into a calendar reminder for the right team member. 'Send updated metrics by Friday' becomes a tracked todo instead of a hope.",
      },
      {
        icon: "Clock",
        heading: "Last-contact field keeps leads warm",
        body: "Each investor card displays the date of the last logged interaction. Sort the pipeline by 'longest since contact' and the cooling conversations rise to the top — the single most effective fundraising hygiene habit, automated.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Log notes from the investor card",
        body: "Open any investor profile, click 'Add note', pick the type (meeting, call, email summary, internal), write or paste the content and save. The note is timestamped, attributed and indexed for search immediately.",
      },
      {
        step: 2,
        title: "Tag and pin what matters",
        body: "Internal tags (interested, lukewarm, declined, decision-maker, intro requested) let you filter the whole pipeline by sentiment. Pinning surfaces the most important context at the top of the card so the next conversation starts informed.",
      },
      {
        step: 3,
        title: "Stay warm with last-contact and reminders",
        body: "The last-contact date updates automatically whenever you log a note, send an email or move the card. Combined with reminder dates on individual notes, no warm investor slips past the radar quietly.",
      },
    ],
    mockup: {
      kind: "notes",
      caption:
        "Notes view inside an investor card showing pinned items, a recent meeting summary and an upcoming reminder.",
    },
    benefits: [
      {
        heading: "Every conversation starts from the right context",
        body: "Walk into the second call already knowing what the partner pushed back on, what numbers they asked for and what objection they want answered. Investors notice immediately, and trust compounds.",
      },
      {
        heading: "Team-wide visibility, zero handover loss",
        body: "Co-founders, advisors and finance leads see the same notes the same way. When one founder is travelling, another can take the next meeting with full context — a fundraising team that scales without dropping balls.",
      },
      {
        heading: "Institutional memory that survives the round",
        body: "When you raise again in 18 months, the notes from this round are still there. The next conversation with the same partner picks up from a real record, not from 'I think we spoke once'.",
      },
    ],
    faqs: [
      {
        question: "Can I copy notes from existing tools like Notion or Google Docs?",
        answer:
          "Yes. Paste freely from any source — formatting, lists and headings are preserved. Bulk import from CSV is also available for founders consolidating from a spreadsheet-based system.",
      },
      {
        question: "Are notes visible to investors?",
        answer:
          "No. All notes are internal-only and visible exclusively to your founding team and any explicitly invited advisors. Investors never see notes about themselves — the notes module is strictly your private workspace.",
      },
      {
        question: "Can I search across all notes?",
        answer:
          "Yes. Full-text search across every note in your CRM with filters for author, date range, investor and tag. Searching 'climate tech valuation 50' will surface every conversation where any of those came up.",
      },
      {
        question: "How do follow-up reminders work?",
        answer:
          "Add a reminder date to any note, optionally assign it to a co-founder, and the reminder appears in the CAPLINK calendar and in the assignee's todo list. A daily digest email summarizes upcoming reminders.",
      },
      {
        question: "Is there an audit trail for edits?",
        answer:
          "Yes. Every edit and deletion is logged with author and timestamp, and notes are soft-deleted by default so nothing is permanently lost without an explicit purge action.",
      },
    ],
    closing:
      "Investor notes are the quiet backbone of a professional fundraising operation. CAPLINK makes them effortless to capture and impossible to lose.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "deal-flow",
    path: "/crm/deal-flow",
    metaTitle: "Centralized Deal Flow Management for Startups | CAPLINK CRM",
    metaDescription:
      "See every active fundraising round in one filterable deal-flow view — by investor type, ticket size, geography and stage — connected to your pipeline, dataroom and pitchdeck.",
    h1: "Deal Flow: Every Active Round in One Filterable View",
    heroSubtitle:
      "Centralized deal-flow across all your fundraising processes, filterable by investor type, ticket size, geography and stage — connected to pipeline, dataroom and pitchdeck.",
    excerpt:
      "Filterable centralized deal flow connected to CRM pipeline, dataroom and pitchdeck manager.",
    intro:
      "A growing startup rarely runs one clean round at a time. You have a primary Series A pipeline, a parallel bridge conversation, a strategic corporate VC track and maybe an SPV being syndicated by an angel — each with its own investors, documents and timelines. CAPLINK's deal-flow view collapses all of those into a single filterable surface, so a founder can answer 'what is happening this week across every active raise?' in under five seconds. Each deal is connected upstream to the pipeline that owns it and downstream to the dataroom and pitchdeck served to that specific investor segment.",
    features: [
      {
        icon: "Layers",
        heading: "All active rounds in one place",
        body: "Primary round, bridge, SPV, secondary — every active deal sits in one centralized view rather than four scattered spreadsheets. Switch between deals with a tab, or look at the unified board to see everything at once.",
      },
      {
        icon: "Filter",
        heading: "Filter by every dimension that matters",
        body: "Filter the deal list by investor type (VC, family office, angel, strategic), ticket size band, geography and current stage. Save filter combinations as views — for example, 'European angels writing under €100k' — and switch between them instantly.",
      },
      {
        icon: "FolderOpen",
        heading: "Connected to dataroom and pitchdeck",
        body: "Each deal is linked to the specific dataroom permissions and pitchdeck version served to that investor segment. Open a deal, see the documents that investor has access to, and update permissions without leaving the view.",
      },
      {
        icon: "Users",
        heading: "Connected to the CRM pipeline",
        body: "Every deal-flow card opens straight into the corresponding pipeline, with all notes, outreach history and stage progression attached. The deal-flow view is the map; the pipeline is the territory.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Configure each active round as a deal",
        body: "Create a deal for every parallel fundraising motion (primary Series A, bridge, strategic corporate VC, etc.) with its own target amount, timeline and assigned team members. Each deal becomes a filterable surface in the unified view.",
      },
      {
        step: 2,
        title: "Apply filters to find the segment you need",
        body: "Filter by investor type, ticket size, geography or stage to drill into a specific cohort — for example, 'US growth-stage VCs in the Due Diligence column of the primary round'. Saved views make recurring filters one click away.",
      },
      {
        step: 3,
        title: "Act directly from the deal-flow view",
        body: "Update dataroom permissions, advance a pipeline stage, send a follow-up — every action lands on the right pipeline card with full audit trail, without forcing the founder to jump between three different tools.",
      },
    ],
    mockup: {
      kind: "dealflow",
      caption:
        "Unified deal-flow view showing three active rounds side by side with shared filters and per-deal progress bars.",
    },
    benefits: [
      {
        heading: "End the 'which spreadsheet?' confusion",
        body: "Founders running parallel rounds typically maintain three or four separate trackers. CAPLINK collapses them into one canonical view, so leadership decisions are made from one set of numbers instead of three reconciled versions.",
      },
      {
        heading: "Faster cross-deal insight",
        body: "Spot patterns instantly — 'every US fund is stuck in Due Diligence longer than every European fund' — and adjust your strategy before the issue compounds. Cross-cutting analytics are impossible in fragmented systems.",
      },
      {
        heading: "Cleaner investor experience",
        body: "Because dataroom and pitchdeck are linked per deal, an investor in your bridge round never accidentally sees primary-round documents and vice versa. Permissions stay clean and audit-ready.",
      },
    ],
    faqs: [
      {
        question: "Can I run primary and bridge rounds simultaneously?",
        answer:
          "Yes. Each round is a separate deal with its own pipeline, dataroom permissions and pitchdeck version, and they appear side by side in the unified deal-flow view. The same investor can appear in two deals if relevant, with separate stage tracking per deal.",
      },
      {
        question: "Does this work for SPVs and syndicates?",
        answer:
          "Yes. SPV and syndicate deals are first-class deal types with their own configuration (lead investor, ticket cap, committed amount tracking). See the investor-syndicates-spv guide on the cap table side for the equity-level mechanics.",
      },
      {
        question: "How does this integrate with the dataroom?",
        answer:
          "Each deal links to a specific dataroom folder (or sub-folder set) and a specific permission group. Granting an investor deal access automatically grants the right document permissions, and revoking deal access revokes them — no manual sync.",
      },
      {
        question: "Can I see historical deals after they close?",
        answer:
          "Yes. Closed deals are archived to the historical deal-flow view with all metadata, notes and document history preserved. When you raise next, the prior deal is one click away as reference context.",
      },
      {
        question: "Who on my team can see which deals?",
        answer:
          "Per-deal access control. Co-founders see everything by default; advisors and external collaborators can be scoped to specific deals only, so a corporate VC track stays confidential from the broader cap-table audience.",
      },
    ],
    closing:
      "Deal flow is the founder's situation board. When every active round shows up in one place, decision quality goes up and execution speed follows.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "auto-notifications",
    path: "/crm/auto-notifications",
    metaTitle: "Automatic Investor Notifications & Alerts | CAPLINK CRM",
    metaDescription:
      "Automatic email alerts to investors when CRM events happen — dataroom access, document updates, stage changes — with customizable triggers, opt-in toggles and branded templates.",
    h1: "Auto-Notifications: Keep Investors Informed Without Manual Email",
    heroSubtitle:
      "Customizable, branded email alerts fire automatically when CRM events occur — dataroom access, document updates, new round opened, deal stage changed — with per-investor opt-in.",
    excerpt:
      "Customizable triggers per investor, opt-in toggles, branded email templates and full notification log.",
    intro:
      "The single biggest source of friction between founders and investors during a round is not the negotiation — it is the silence between events. Investors who do not hear from you assume the deal is dying. Founders who manually email every investor every time something changes lose entire days to status updates. CAPLINK's auto-notification engine fires the right branded message to the right investor at the right CRM event, automatically: granting dataroom access, refreshing a document, opening a new round, advancing a stage. The result is a fundraising process that feels disciplined and proactive from the investor side, without the founder writing a single status email.",
    features: [
      {
        icon: "Bell",
        heading: "Event-driven triggers",
        body: "Notifications fire on real CRM events — dataroom permission granted, document re-uploaded, deal stage advanced, new round opened, meeting scheduled. The trigger is the underlying action, so there is nothing to remember to send manually.",
      },
      {
        icon: "Sparkles",
        heading: "Customizable per investor",
        body: "Every trigger can be configured per investor or per investor segment. A strategic CVC might get notifications on document updates only, while a lead investor gets every event. Opt-in toggles let an investor mute specific channels without leaving the deal.",
      },
      {
        icon: "Shield",
        heading: "Branded email templates",
        body: "Every notification ships from your authenticated domain in your brand — logo, colors, footer, signature block. Investors never receive a generic-looking platform email; they receive what looks like a personal note from your team, automatically generated.",
      },
      {
        icon: "Eye",
        heading: "Complete notification log",
        body: "Every notification is logged with timestamp, recipient, trigger event and delivery status. Investor disputes 'I never got that' arguments stop instantly — the log is the source of truth, exportable for compliance and audit.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Pick the triggers that matter for your round",
        body: "From the notification settings, enable the triggers relevant to this round — dataroom access granted, document updated, deal stage changed, meeting scheduled. Defaults are sensible; customization is granular.",
      },
      {
        step: 2,
        title: "Configure per-investor preferences",
        body: "By default every investor in the pipeline receives the configured triggers. For specific investors or segments, customize which triggers fire, in which language and with which template variant.",
      },
      {
        step: 3,
        title: "Let CRM events drive the cadence",
        body: "Update a document in the dataroom, advance a stage, schedule a call — the corresponding notification fires automatically to the right investors. The notification log records every send, open and click for full visibility.",
      },
    ],
    mockup: {
      kind: "notifications",
      caption:
        "The notification settings panel showing per-trigger controls and a sample branded email preview.",
    },
    benefits: [
      {
        heading: "Investors feel informed without you writing emails",
        body: "Every relevant change reaches the relevant investor within minutes, branded and personalized. The qualitative signal investors receive is 'this team has their act together' — exactly the signal that wins term sheets.",
      },
      {
        heading: "Hours of founder time recovered every week",
        body: "The math is simple: 30 investors × 4 events per week × 2 minutes per status email = four hours of founder time per week, eliminated. Across a six-month round that is over 100 hours back.",
      },
      {
        heading: "Audit-ready notification history",
        body: "Every notification is logged and exportable. When questions come up during due diligence or post-close compliance review, the evidence trail is complete and instantly queryable.",
      },
    ],
    faqs: [
      {
        question: "Can investors opt out of notifications?",
        answer:
          "Yes. Each notification email includes a per-channel opt-out link. Investors can mute document-update notifications while still receiving deal-stage notifications, or vice versa, without affecting their access to the deal itself.",
      },
      {
        question: "Are notifications sent from my domain or from CAPLINK?",
        answer:
          "From your authenticated domain. SPF, DKIM and DMARC are configured during setup, so notifications appear to investors as direct emails from your team and replies route to your inbox.",
      },
      {
        question: "Can I localize notifications by investor language?",
        answer:
          "Yes. Template variants per language (English, German, French, Spanish, etc.) are configurable per investor. The right language fires automatically based on the investor's profile setting.",
      },
      {
        question: "What happens if a notification bounces?",
        answer:
          "Bounces are flagged in the notification log against the specific investor address (not the entire campaign). The pipeline card surfaces a warning so the founder can update the contact email and resend the missed event manually.",
      },
      {
        question: "Are notification templates customizable?",
        answer:
          "Fully. Every template — subject, body, footer — supports variables (investor name, deal name, stage, document name) and your brand styling. You can also create per-round template variants for one-off campaigns.",
      },
    ],
    closing:
      "Auto-notifications are the silent professionalism layer of the CAPLINK CRM. Investors notice — and founders get their week back.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "instant-messaging",
    path: "/crm/instant-messaging",
    metaTitle: "In-App Investor Messaging & Chat | CAPLINK CRM",
    metaDescription:
      "Real-time chat directly inside investor profiles with read receipts and full message history tied to the investor record — every conversation in context, never lost in email.",
    h1: "Instant Messaging: Real-Time Chat Inside Every Investor Profile",
    heroSubtitle:
      "In-app messaging built into each investor card — real-time, read receipts, full history attached to the CRM record so context never gets lost in email threads.",
    excerpt:
      "Real-time messaging directly within investor profiles with read receipts and full message history tied to the investor record.",
    intro:
      "Some investor conversations belong in email. Many do not. A quick clarification, a document pointer, a 'are we still on for Thursday?' — these belong in a faster channel, and the founders who use a faster channel close faster. The problem with WhatsApp or Slack-Connect is that the conversation lives outside the CRM, disconnected from notes, pipeline stage and dataroom activity. CAPLINK's instant messaging keeps the speed of chat while landing every message directly inside the investor's pipeline card, where it is searchable, attributable and part of the deal history forever.",
    features: [
      {
        icon: "MessageSquare",
        heading: "Real-time chat per investor",
        body: "Open any investor card and the messaging panel is right there — typing indicators, instant delivery, message history scrollable in line with notes and pipeline events. No app switching, no separate login for the investor.",
      },
      {
        icon: "Eye",
        heading: "Read receipts and presence",
        body: "See when an investor has read your last message and when they are typing a reply. Founders waste less time wondering 'did they see it?' and investors get the implicit nudge of presence without the awkwardness of a follow-up email.",
      },
      {
        icon: "Layers",
        heading: "Full history tied to the CRM record",
        body: "Every message is permanently attached to the investor's pipeline card. When you raise the next round, the chat history with this investor is still there, alongside notes, emails and deal activity — total continuity across years.",
      },
      {
        icon: "Bell",
        heading: "Push and email fallback",
        body: "Messages trigger in-app and push notifications, with email fallback after a configurable delay so nothing is missed when the investor is offline. The fallback is branded and links back into the chat thread.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Invite the investor to chat",
        body: "From the investor card, send a chat invite. The investor accepts via a one-click magic link — no signup friction — and the chat thread opens inside the deal context, with the relevant pitchdeck and dataroom one click away.",
      },
      {
        step: 2,
        title: "Message in real time",
        body: "Type, send, see read receipts. Attach documents, link to specific dataroom files, mention co-founders to bring them into the thread. The conversation feels like Slack, scoped to one investor.",
      },
      {
        step: 3,
        title: "Reference the history anytime",
        body: "Months later, scroll the chat thread on the investor card to recover the exact phrasing of a clarification or the link to a specific document. No 'I think we discussed this on WhatsApp' uncertainty.",
      },
    ],
    mockup: {
      kind: "chat",
      caption:
        "In-app messaging panel inside an investor profile, with read receipts and a linked dataroom document.",
    },
    benefits: [
      {
        heading: "Faster turns, faster closes",
        body: "Quick clarifications happen in seconds rather than days. The cumulative effect across a round is often two to three weeks shaved off the close date.",
      },
      {
        heading: "Conversation context never leaves the CRM",
        body: "Unlike WhatsApp threads that vanish when a phone is reset, every message is permanently stored against the investor record, searchable, exportable and visible to the founding team.",
      },
      {
        heading: "Investors get one professional channel",
        body: "Instead of bouncing between email, WhatsApp, LinkedIn and Telegram, the investor has one chat interface attached to the deal. The professionalism shows.",
      },
    ],
    faqs: [
      {
        question: "Do investors need a CAPLINK account?",
        answer:
          "No. Investors join chats via a magic link that creates a lightweight investor identity scoped to your deal. No password setup, no app installation, no friction. Investors who participate in multiple CAPLINK deals across different startups have a unified inbox.",
      },
      {
        question: "Is the chat end-to-end encrypted?",
        answer:
          "Messages are encrypted in transit and at rest. End-to-end encryption is on the roadmap; current implementation uses platform-managed keys with strict access controls and audit logging.",
      },
      {
        question: "Can I bring co-founders into a chat thread?",
        answer:
          "Yes. Mention any co-founder in the thread and they are notified and added. Group chats with multiple founders and multiple investor team members are fully supported.",
      },
      {
        question: "Can I export chat history for compliance?",
        answer:
          "Yes. Per-investor and per-deal chat history is exportable to PDF or JSON with full metadata. Useful for due-diligence audits and post-close record-keeping.",
      },
      {
        question: "Does messaging integrate with notifications and notes?",
        answer:
          "Yes. Important messages can be pinned as notes with one click, and message events appear in the investor timeline alongside stage changes and document activity. Everything stays connected.",
      },
    ],
    closing:
      "Instant messaging closes the speed gap between email and Slack, with the auditability of a real CRM. Founders who use it close noticeably faster.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "investor-timeline",
    path: "/crm/investor-timeline",
    metaTitle: "Investor Timeline & Fundraising Milestones | CAPLINK CRM",
    metaDescription:
      "Standard or fully custom fundraising timelines with milestones linked to investor actions, meetings and documents — connected to the calendar for both founders and investors.",
    h1: "Investor Timeline: Standard or Custom Milestones, Visual Progress",
    heroSubtitle:
      "Two modes — a standard timeline with predefined fundraising milestones or a fully custom one. Milestones link to investor actions, meetings and documents.",
    excerpt:
      "Standard or custom timelines with milestones linked to investor actions, meetings and documents.",
    intro:
      "Every fundraising round has a structure, but no two rounds run on the same schedule. A founder raising a tight three-month Series A with a designated lead needs a different timeline than a founder running a six-month rolling Seed with a syndicate of angels. CAPLINK's investor timeline supports both — start from a predefined template that mirrors a standard fundraising motion, or build a fully custom timeline milestone by milestone. Every milestone is connected to real artifacts in the system: investor actions, scheduled meetings, document uploads, calendar entries — so the timeline is a live progress indicator, not a static slide.",
    features: [
      {
        icon: "Clock",
        heading: "Standard or fully custom modes",
        body: "Pick a predefined template (Seed, Series A, Series B, Bridge) and adjust dates, or start from a blank slate and add custom milestones. The same investor card can carry both a general timeline (round-level) and an individual one (per-investor commitments).",
      },
      {
        icon: "Calendar",
        heading: "Calendar integration both sides",
        body: "Every milestone with a date flows into the CAPLINK calendar for the founder side, and into the investor's calendar view for items they need to act on. Both sides see the same dates, eliminating scheduling drift.",
      },
      {
        icon: "Layers",
        heading: "Milestones linked to real actions",
        body: "A 'partner pitch' milestone links to the meeting record, the deck served, the notes captured. A 'dataroom opened' milestone links to the permissions grant. The timeline is a navigation surface into the underlying deal, not a separate document.",
      },
      {
        icon: "Eye",
        heading: "Investor sees only their relevant deadlines",
        body: "Investors viewing the timeline see the general deadlines (round close) and their individual deadlines (their LOI by date X), but never another investor's commitments. Confidentiality is preserved by default.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Choose standard or custom mode",
        body: "Standard mode applies a predefined Seed/Series A/Series B template with editable dates. Custom mode lets you define each milestone from scratch — name, date, owner, linked artifact.",
      },
      {
        step: 2,
        title: "Link milestones to deal artifacts",
        body: "Attach each milestone to the corresponding meeting, document or stage transition. A dataroom-opening milestone, for instance, can auto-trigger when the permissions are granted, keeping the timeline accurate without manual updates.",
      },
      {
        step: 3,
        title: "Share the right slice with each investor",
        body: "Each investor's view of the timeline shows the general round-level milestones plus their own individual deadlines. The founder team sees everything; investors see only what concerns them.",
      },
    ],
    mockup: {
      kind: "timeline",
      caption:
        "A visual timeline showing five milestones across a 12-week Series A motion with one overdue indicator.",
    },
    benefits: [
      {
        heading: "Round predictability investors trust",
        body: "When investors can see a credible timeline with real dates and live progress, the perceived risk of the deal drops. Predictability is a fundraising asset.",
      },
      {
        heading: "Internal alignment without status meetings",
        body: "Co-founders, advisors and the broader team see the same milestones with the same dates. The weekly 'where are we?' standup becomes a 30-second look at the timeline.",
      },
      {
        heading: "Investor experience that signals professionalism",
        body: "Investors who see a real timeline (with their own deadline highlighted) take the deal more seriously. The implicit message is: this founder runs a professional process.",
      },
    ],
    faqs: [
      {
        question: "Can I have different timelines for different investors?",
        answer:
          "Yes. The general timeline is shared across the round, but each investor card carries an individual timeline overlay for the specific commitments unique to that investor (LOI date, wire date, board confirmation, etc.).",
      },
      {
        question: "How does timeline integrate with the calendar module?",
        answer:
          "Every dated milestone appears in the CAPLINK calendar with appropriate filters. Meetings on the timeline are full calendar events; document deadlines are reminders; commitments are blocked time entries — all in one calendar surface.",
      },
      {
        question: "Can investors edit their own deadlines?",
        answer:
          "Investors can request a change, which routes to the founder for approval. They cannot unilaterally edit the timeline — preserving the founder's control over the round narrative.",
      },
      {
        question: "What happens to the timeline after close?",
        answer:
          "Closed timelines are archived with the deal record and become reference material for the next round. The team can review which milestones slipped, which were ahead of schedule, and use that data to plan the next raise.",
      },
      {
        question: "Can I import a timeline from a project tool?",
        answer:
          "Yes. Import from CSV, Notion or a project management export. The timeline builder accepts bulk milestone uploads with name, date and owner fields.",
      },
    ],
    closing:
      "The investor timeline gives every round a backbone. Predictability and professionalism follow automatically.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "todo-management",
    path: "/crm/todo-management",
    metaTitle: "Fundraising Todo Lists & Task Management | CAPLINK CRM",
    metaDescription:
      "Phase-bound task lists built into every fundraising stage. Per-investor or per-milestone todos, recurring templates and due-date reminders that help founders run a professional process.",
    h1: "Todo Management: Phase-Bound Tasks Built Into Every Stage",
    heroSubtitle:
      "Per-investor and per-milestone todos with recurring templates and due-date reminders, so every fundraising stage runs as a checklist instead of a memory exercise.",
    excerpt:
      "Phase-bound task lists built into every fundraising stage with recurring templates and due-date reminders.",
    intro:
      "Fundraising is a sequence of small, repeated, easy-to-forget tasks done across dozens of investors over months. Forget one — the NDA you meant to send before the dataroom invite, the reference call you promised on Friday, the deck update before the next partner meeting — and momentum dies. CAPLINK's todo management makes the small things impossible to drop: every fundraising stage carries a built-in checklist, every investor card carries per-investor tasks, and recurring templates ensure the same standard process runs for every conversation, not just the ones the founder remembers.",
    features: [
      {
        icon: "ListTodo",
        heading: "Todos per stage and per investor",
        body: "Every Kanban stage carries a default checklist (Sent Teaser stage: 'NDA confirmed', 'Follow-up scheduled', 'Partner profile reviewed'). Every investor card carries its own per-investor todos for the specific commitments and reminders unique to that conversation.",
      },
      {
        icon: "Sparkles",
        heading: "Recurring task templates",
        body: "Define a template once (your standard Series A process) and apply it to every new investor entering the pipeline. Every conversation runs the same disciplined sequence, so quality does not depend on the founder remembering the playbook each time.",
      },
      {
        icon: "Bell",
        heading: "Due-date reminders and escalation",
        body: "Each todo has an optional due date that drives email and in-app reminders. Overdue todos escalate visually on the investor card and in a centralized 'today' view, so nothing slips silently.",
      },
      {
        icon: "Users",
        heading: "Assign across the founding team",
        body: "Todos can be assigned to any team member with notifications and accountability tracking. The founder running outreach assigns dataroom prep to the operations co-founder; both see the status without a separate Slack thread.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Configure your stage templates once",
        body: "For each pipeline stage, define the default todos every investor should trigger when they enter that stage. The templates are reusable across rounds, so the playbook compounds.",
      },
      {
        step: 2,
        title: "Add per-investor todos as they come up",
        body: "On any investor card, add ad-hoc todos for the unique commitments — 'send revenue breakdown by segment by Tuesday', 'intro to operating partner via LinkedIn'. Each todo carries owner, due date and status.",
      },
      {
        step: 3,
        title: "Work from the today view, not the pipeline",
        body: "The centralized today view aggregates every overdue and due-today todo across every investor and every round. Founders start the day on the todo list, not on the Kanban, and the pipeline advances as a consequence.",
      },
    ],
    mockup: {
      kind: "todos",
      caption:
        "Centralized today view showing 12 todos across three rounds with overdue indicators and assigned owners.",
    },
    benefits: [
      {
        heading: "A professional process every investor sees",
        body: "When the same checklist runs for every investor, every investor receives the same disciplined experience. Investors notice and reciprocate with similarly disciplined behavior.",
      },
      {
        heading: "Founders sleep better",
        body: "The mental load of 'what was I supposed to do for investor X?' disappears when the system holds every todo with a due date and reminder. Cognitive bandwidth returns to actual decision-making.",
      },
      {
        heading: "Onboarding the next team member takes minutes",
        body: "When a new co-founder, advisor or operations hire joins the round, the stage templates and per-investor todos onboard them implicitly. The playbook is the system.",
      },
    ],
    faqs: [
      {
        question: "Can I import existing todo lists?",
        answer:
          "Yes. Import from CSV, Notion or a project management tool — owner, due date and stage map directly. Existing playbooks become CAPLINK stage templates in minutes.",
      },
      {
        question: "Do todos integrate with the calendar?",
        answer:
          "Dated todos appear as calendar reminders in CAPLINK and via standard ICS feed to Google Calendar, Outlook and Apple Calendar. The founder's external calendar reflects fundraising commitments alongside everything else.",
      },
      {
        question: "Can I make todos visible to investors?",
        answer:
          "By default todos are internal-only. Specific todos can be marked investor-visible (for example, deliverables the investor is waiting for), which surfaces them on the investor's side of the deal view.",
      },
      {
        question: "What about recurring todos like weekly investor updates?",
        answer:
          "Recurring todos are supported with daily, weekly, monthly or custom cadence. Common patterns — Friday investor update email, Monday pipeline review — are configurable once and persist for the duration of the round.",
      },
      {
        question: "How are overdue todos escalated?",
        answer:
          "Overdue todos appear in red on the relevant investor card, in the today view, and in a daily digest email summarizing what slipped. Optional escalation rules can notify a co-founder when a todo is more than 48 hours overdue.",
      },
    ],
    closing:
      "Todos are the operating discipline of a professional fundraising process. CAPLINK makes the discipline structural instead of dependent on founder memory.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "mass-outreach-templates",
    path: "/crm/mass-outreach-templates",
    metaTitle: "Investor Outreach Email Templates Library | CAPLINK CRM",
    metaDescription:
      "Stage-specific outreach templates — Teaser & NDA, Pitchdeck, Follow-ups, Reminders — fully customizable with platform variables, branding and AI enrichment for 1:1-quality emails.",
    h1: "Mass Outreach Templates: A Stage-Specific Email Library",
    heroSubtitle:
      "Stage-by-stage templates for Teaser & NDA, Pitchdeck, Follow-ups and Reminders — auto-populated from your startup profile and enriched by AI to feel personally written.",
    excerpt:
      "Stage-specific templates with auto-pulled startup data, full customization, founder-created templates and AI-powered personalization.",
    intro:
      "The right outreach email at the right stage is half the battle. The teaser sent to a cold investor is structurally different from the follow-up sent after a partner meeting, which is different from the reminder sent ten days into silence. CAPLINK ships with a curated library of stage-specific templates that map exactly to the pipeline stages, auto-populate from your startup profile, support deep variable substitution and pass through the AI personalization layer so each send reads like a 1:1 message. Founders can fully customize the included templates or build their own from scratch.",
    features: [
      {
        icon: "FileText",
        heading: "Templates for every stage",
        body: "Teaser & NDA, Pitchdeck Introduction, Follow-up After Meeting, Reminder, Reference Request, Final Push — every standard fundraising message has a tuned starting point. Founders almost never write a fundraising email from a blank page.",
      },
      {
        icon: "Sparkles",
        heading: "Auto-pulled startup data",
        body: "Templates automatically fill startup description, ARR, growth rate, team size and round details from your platform profile. Update the profile once and every template across the library reflects the change — no manual find-and-replace.",
      },
      {
        icon: "Layers",
        heading: "Custom templates and variables",
        body: "Build your own templates for unique workflows and define custom variables (investor focus, partner name, prior portfolio fit) that the system substitutes per recipient. Variables can pull from the investor profile, the deal record or the founder's own profile.",
      },
      {
        icon: "Shield",
        heading: "Branding and AI enrichment",
        body: "Every template ships in your brand — logo, signature block, color accents — and passes through the AI enrichment layer so each individual send is rewritten with investor-specific context. Templates are the skeleton; AI is the soul.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Browse the curated template library",
        body: "Open the template library, filter by stage (Teaser, Pitchdeck, Follow-up, Reminder) and language. Each template includes a preview with sample data so you see exactly what an investor receives.",
      },
      {
        step: 2,
        title: "Customize or build from scratch",
        body: "Edit any template inline, save the variant under your team library, or build a brand-new template using the variable picker and rich-text editor. Custom templates are usable by every member of your founding team.",
      },
      {
        step: 3,
        title: "Apply to a campaign with AI enrichment",
        body: "When launching a mass outreach campaign, pick the template, confirm the variable mappings, and let the AI generate the per-investor variant. Preview a sample, approve the batch, and the system handles the rest.",
      },
    ],
    mockup: {
      kind: "templates",
      caption:
        "Template library with stage filters, preview pane and AI-enrichment toggle highlighted.",
    },
    benefits: [
      {
        heading: "Hours saved per campaign",
        body: "Founders typically spend two to three hours drafting outreach. With templates, the active time drops to under thirty minutes, and quality goes up because the starting point is a well-tested structure.",
      },
      {
        heading: "Consistent voice across the team",
        body: "When co-founders, advisors and operations leads all draw from the same template library, the outbound voice stays consistent. Investors who receive emails from different team members get one coherent brand experience.",
      },
      {
        heading: "Compound improvement over time",
        body: "As you A/B test subject lines and structures, the winners get saved back to the library. Every round starts from a better baseline than the last — the playbook gets sharper instead of starting from scratch.",
      },
    ],
    faqs: [
      {
        question: "What templates ship out of the box?",
        answer:
          "Teaser introduction, NDA request, Pitchdeck cover note, Post-meeting follow-up, Silence reminder, Reference call request, Term-sheet acknowledgement, Round-close announcement and Investor update — each in multiple languages and tone variants (formal/casual).",
      },
      {
        question: "Can I version templates?",
        answer:
          "Yes. Every template carries a version history with edit author and timestamp. Roll back to a prior version with one click if a recent edit underperforms.",
      },
      {
        question: "How does AI enrichment differ from a generic mail-merge?",
        answer:
          "Mail-merge substitutes variables ({first_name}, {company}). AI enrichment rewrites entire paragraphs in your voice using investor-specific context (their portfolio, their stage preference, their recent thesis posts), so the personalization is contextual rather than mechanical.",
      },
      {
        question: "Can I disable AI enrichment for a specific campaign?",
        answer:
          "Yes. Toggle AI enrichment off and the template ships with variable substitution only. Useful for highly formal communications where exact phrasing matters more than personalization.",
      },
      {
        question: "Are templates shared across multiple rounds?",
        answer:
          "Yes. Your team template library persists across rounds, so the playbook you build during Seed is available verbatim during Series A. Templates accumulate institutional value.",
      },
    ],
    closing:
      "Templates plus AI enrichment turn outreach from a per-investor writing exercise into a per-investor approval exercise. The leverage is enormous.",
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    slug: "crm-pipeline-reports",
    path: "/crm/crm-pipeline-reports",
    metaTitle: "Fundraising Pipeline Reports for Shareholders | CAPLINK CRM",
    metaDescription:
      "Auto-generated professional fundraising reports for existing shareholders — investor stages, milestones and notes pulled directly from CAPLINK, exported as branded PDF.",
    h1: "Pipeline Reports: Shareholder-Ready Fundraising Status in One Click",
    heroSubtitle:
      "Auto-generated professional reports for existing shareholders that summarize the live fundraising process — stages, milestones, notes per investor — exported as a branded PDF.",
    excerpt:
      "Auto-pulled investor stages, milestones and notes per investor, exportable as branded PDF for existing shareholders.",
    intro:
      "Existing shareholders need to know how the round is going. The honest path — a manual monthly slide deck stitched together from a Notion board and a Google Sheet — eats half a founder day each cycle. CAPLINK's pipeline reports replace that exercise with a one-click generation: the system pulls investor stages, milestones and per-investor notes directly from your live CRM data, formats them into a professional shareholder-ready document and exports as a branded PDF you can attach to your investor update or send as a standalone report.",
    features: [
      {
        icon: "BarChart3",
        heading: "Live data, zero manual stitching",
        body: "Every section of the report — pipeline summary, stage distribution, top conversations, key milestones — is pulled live from your CRM at generation time. There is no spreadsheet to update first and no risk of stale numbers.",
      },
      {
        icon: "FileText",
        heading: "Per-investor narrative",
        body: "The report includes a per-investor section summarizing current stage, last contact date and a curated subset of notes. Founders pick which investors to include (lead, top-of-funnel, strategic) without rewriting any content.",
      },
      {
        icon: "Shield",
        heading: "Branded PDF export",
        body: "Reports export as a professionally laid-out PDF with your logo, colors and footer. Shareholders receive a document that looks like a polished investor update, not a spreadsheet print-out.",
      },
      {
        icon: "Eye",
        heading: "Confidentiality controls",
        body: "Per-investor inclusion is opt-in, and the report can be redacted to anonymize specific investors or hide notes flagged as sensitive. Shareholders see enough to be informed, never more than the founder intends.",
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: "Configure the report scope",
        body: "Pick the round, the reporting period (weekly, monthly, ad-hoc) and the investors to include. Decide which sections to show — summary, per-investor narrative, milestones, next steps.",
      },
      {
        step: 2,
        title: "Generate the report",
        body: "One click pulls the live CRM data and assembles the PDF. Generation takes seconds. Preview the output in the browser and adjust the scope if needed before exporting.",
      },
      {
        step: 3,
        title: "Distribute to shareholders",
        body: "Download the branded PDF and attach to your investor update, or send via the CAPLINK investor-update module to the configured shareholder distribution list. Distribution is tracked.",
      },
    ],
    mockup: {
      kind: "reports",
      caption:
        "Report preview showing pipeline summary chart, top investor section and an upcoming-milestones table on a branded page.",
    },
    benefits: [
      {
        heading: "Half a founder day back, every cycle",
        body: "Replacing the manual stitching with a one-click generation typically saves three to four hours per shareholder update cycle. Across a year that is a full work-week of founder time recovered.",
      },
      {
        heading: "Higher-quality shareholder communication",
        body: "When reports are easy, they happen more often. Shareholders who get regular, well-formatted reports stay supportive and act as advocates when the round needs an extra introduction.",
      },
      {
        heading: "Audit trail for governance",
        body: "Every generated report is archived with timestamp and scope, creating a chronological record of fundraising communication that is invaluable during due diligence on the next round.",
      },
    ],
    faqs: [
      {
        question: "Can I customize the report layout?",
        answer:
          "Yes. Choose from multiple layout templates (one-page summary, multi-page detailed, executive deck) and adjust branding (logo, colors, fonts) per round. Custom sections can be added for unique reporting requirements.",
      },
      {
        question: "How are sensitive notes filtered out?",
        answer:
          "Notes flagged 'internal only' are automatically excluded. Investors flagged 'confidential' do not appear in shareholder reports. Founders see a preview and can manually exclude additional content before exporting.",
      },
      {
        question: "Can shareholders view reports interactively online?",
        answer:
          "Yes. In addition to PDF export, reports can be shared via a secure web link with permission controls — view-only, watermarked, expiring. Useful for governance contexts where the audit trail matters.",
      },
      {
        question: "What data sources feed the report?",
        answer:
          "The investor pipeline, the notes module, the milestone timeline and the campaign analytics. Anything visible in the CRM can be surfaced in the report; nothing outside the CRM is included.",
      },
      {
        question: "How often can I generate reports?",
        answer:
          "Unlimited. Generate weekly, monthly or ad-hoc. Each generation captures the live state at that moment, so the historical archive shows the evolution of the round over time.",
      },
    ],
    closing:
      "Pipeline reports turn shareholder communication from a chore into a one-click exercise. The result: better governance, less founder time, stronger advocacy from the cap table.",
  },
];

export function findCrmGuide(slug: string | undefined): CrmGuideEntry | undefined {
  if (!slug) return undefined;
  return CRM_GUIDES.find((g) => g.slug === slug);
}
